<template>
  <div class="esra-rating-status">
    <div v-if="rating" class="esra-rating-status__rating">
      <span class="esra-rating-status__age" dir="ltr">{{ rating.age }}</span>

      <img
        v-if="rating.ageIcon1"
        :src="rating.ageIcon1"
        :alt="$t('APPS.INFO.ESRA.ICON_ALT')"
        class="esra-rating-status__icon"
      />
      <img
        v-if="rating.ageIcon2"
        :src="rating.ageIcon2"
        :alt="$t('APPS.INFO.ESRA.ICON_ALT')"
        class="esra-rating-status__icon"
      />
    </div>

    <div v-if="needsRequest" class="esra-rating-status__request">
      <p
        class="esra-rating-status__message"
        :class="{ 'text-alert': hasStartedRequest || needsRequest }"
      >
        <i class="material-icons esra-rating-status__message-icon">
          error_outline
        </i>
        {{ requestMessage }}
      </p>

      <ElButton
        type="primary"
        size="small"
        native-type="button"
        :loading="isRequesting"
        @click="requestRating"
      >
        {{ requestButtonLabel }}
      </ElButton>
    </div>

    <p
      v-else-if="!rating && !isLoading"
      class="esra-rating-status__message text-secondary"
    >
      {{ $t('APPS.INFO.ESRA.UNAVAILABLE') }}
    </p>

    <ElDialog
      :title="$t('APPS.INFO.ESRA.REQUEST_FAILED_TITLE')"
      :visible="showRequestFailure"
      width="30%"
      @close="showRequestFailure = false"
    >
      <p>{{ $t('APPS.INFO.ESRA.REQUEST_FAILED_DESCRIPTION') }}</p>

      <template slot="footer">
        <ElButton native-type="button" @click="showRequestFailure = false">
          {{ $t('CANCEL') }}
        </ElButton>
        <ElButton
          type="danger"
          native-type="button"
          :loading="isRequesting"
          @click="requestRating"
        >
          {{ $t('RETRY') }}
        </ElButton>
      </template>
    </ElDialog>
  </div>
</template>

<script>
import { Apps } from '@/api/api';

export default {
  name: 'EsraRatingStatus',

  props: {
    packageName: {
      type: String,
      required: true,
    },
    rating: {
      type: Object,
      default: null,
    },
    needsRequest: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isRequesting: false,
      redirectUrl: '',
      showRequestFailure: false,
      shouldRefreshOnFocus: false,
    };
  },

  computed: {
    hasStartedRequest() {
      return Boolean(this.redirectUrl);
    },
    requestMessage() {
      return this.$t(
        this.hasStartedRequest
          ? 'APPS.INFO.ESRA.INCOMPLETE_DESCRIPTION'
          : 'APPS.INFO.ESRA.REQUEST_DESCRIPTION'
      );
    },
    requestButtonLabel() {
      return this.$t(
        this.hasStartedRequest
          ? 'APPS.INFO.ESRA.CONTINUE_REQUEST'
          : 'APPS.INFO.ESRA.REQUEST'
      );
    },
  },

  mounted() {
    window.addEventListener('focus', this.refreshAfterPortalReturn);
  },

  beforeDestroy() {
    window.removeEventListener('focus', this.refreshAfterPortalReturn);
  },

  methods: {
    requestRating() {
      if (this.isRequesting) return Promise.resolve();

      if (this.redirectUrl) {
        this.openPortal(this.redirectUrl);
        return Promise.resolve();
      }

      this.isRequesting = true;
      this.showRequestFailure = false;

      return Apps.requestEsraRating(this.packageName)
        .then(({ redirectUrl }) => {
          if (!redirectUrl) throw new Error('Missing ESRA redirect URL');

          this.redirectUrl = redirectUrl;
          this.openPortal(redirectUrl);
        })
        .catch(() => {
          this.showRequestFailure = true;
        })
        .finally(() => {
          this.isRequesting = false;
        });
    },
    openPortal(url) {
      this.shouldRefreshOnFocus = true;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    refreshAfterPortalReturn() {
      if (!this.shouldRefreshOnFocus) return;

      this.shouldRefreshOnFocus = false;
      this.$emit('refresh');
    },
  },
};
</script>

<style lang="scss">
.esra-rating-status {
  width: 100%;

  &__rating,
  &__request {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $global-margin;
  }

  &__rating + &__request {
    margin-top: $global-margin;
  }

  &__age {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: rem-calc(42);
    min-height: rem-calc(30);
    padding: 0 $global-padding / 2;
    border: 1px solid $lb-light-gray;
    border-radius: rem-calc(16);
    font-weight: bold;
  }

  &__icon {
    max-width: rem-calc(64);
    max-height: rem-calc(48);
    object-fit: contain;
  }

  &__message {
    flex: 1;
    min-width: rem-calc(220);
    margin: 0;
  }

  &__message-icon {
    margin-right: $global-margin / 2;
    font-size: rem-calc(18);
    vertical-align: middle;

    @include rtl() {
      margin-right: 0;
      margin-left: $global-margin / 2;
    }
  }
}
</style>
